import { Component } from '@theme/component';
import { debounce, onAnimationEnd, mediaQueryLarge } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';
import { loadThemes, matchThemes, renderThemeRow, relevanceComparator } from '@theme/sab-search-themes';

/** Products requested while typing; they are sorted by part count and the top 6 (4 on mobile) are shown. */
const PRODUCT_LIMIT = 10;
/** How many cards to try, biggest first, when the panel would be taller than the screen. */
const FIT_STEPS_DESKTOP = [6, 4, 3];
const FIT_STEPS_MOBILE = [4, 3];

/**
 * The search panel: theme suggestions, product results and the empty state.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The results container.
 * @property {HTMLElement} resetButton - The clear button in the input.
 * @property {HTMLElement} themeSuggestions - The theme suggestions container.
 * @property {HTMLElement} footer - The "See all results" footer.
 * @property {HTMLButtonElement} viewAllButton - The "See all results" button.
 * @property {HTMLFormElement} form - The search form.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {import('@theme/dialog').DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.refs.form?.addEventListener('submit', this.#handleSubmit, { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      dialog.refs.dialog?.addEventListener('click', this.#handleOverlayClick, { signal });
      window.addEventListener('resize', this.#handleResize, { signal, passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogOpen = () => {
    this.#updatePanelTop();
    loadThemes(this.dataset.themeSource);

    if (!this.#emptyStateLoaded) {
      this.#emptyStateLoaded = true;
      this.#loadRecentlyViewed();
    }

    this.#fitToScreen();
    requestAnimationFrame(() => this.refs.searchInput.focus());
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * The <dialog> is the dark overlay below the panel: a click on it (not on the panel) closes the panel.
   * @param {MouseEvent} event
   */
  #handleOverlayClick = (event) => {
    if (event.target === this.dialog?.refs.dialog) this.dialog?.closeDialog();
  };

  #handleResize = debounce(() => {
    this.#updatePanelTop();
    this.#fitToScreen();
  }, 100);

  /**
   * Starts the panel (and the overlay) right below the announcement bar, so the bar stays visible.
   */
  #updatePanelTop = () => {
    const dialogElement = this.dialog?.refs.dialog;
    if (!dialogElement) return;

    const announcementBar = document.querySelector('.announcement-bar');
    const bottom = announcementBar ? announcementBar.getBoundingClientRect().bottom : 0;
    dialogElement.style.setProperty('--sab-search-top', `${Math.max(0, Math.round(bottom))}px`);
  };

  /**
   * Blocks submitting an empty search.
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    if (!this.refs.searchInput.value.trim()) event.preventDefault();
  };

  /* ---------------------------------------------------------------- Layout */

  /**
   * Keeps the whole panel on screen: if it would be taller than the space below the announcement bar,
   * show fewer cards (6 → 4 → 3 on desktop, 4 → 3 on mobile) instead of clipping or scrolling.
   */
  #fitToScreen() {
    requestAnimationFrame(() => {
      const dialogElement = this.dialog?.refs.dialog;
      if (!dialogElement?.open) return;

      const body = this.querySelector('.sab-search__body');
      if (!(body instanceof HTMLElement)) return;

      const steps = mediaQueryLarge.matches ? FIT_STEPS_DESKTOP : FIT_STEPS_MOBILE;
      const grids = Array.from(this.querySelectorAll('.sab-search__results .sab-search-grid'));

      // The panel is capped to the screen height, so "too tall" shows up as the body overflowing
      for (const step of steps) {
        for (const grid of grids) {
          if (grid instanceof HTMLElement) grid.dataset.limit = String(step);
        }
        if (body.scrollHeight <= body.clientHeight + 1) break;
      }
    });
  }

  /**
   * Orders the result cards: title/theme matches first, then the rest; biggest set first in each group.
   */
  #sortResults() {
    const grid = this.refs.predictiveSearchResults.querySelector('.sab-search-results .sab-search-grid');
    if (!grid) return;

    const cards = /** @type {HTMLElement[]} */ (Array.from(grid.children));
    cards.sort(relevanceComparator(this.refs.searchInput.value.trim()));
    grid.append(...cards);
  }

  /* ---------------------------------------------------------------- Keyboard */

  /** @returns {HTMLElement[]} Focusable result links in DOM order (theme pills, products, pills). */
  get #navItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('#sab-search-body [data-search-nav-item]')).filter(
        (item) => item instanceof HTMLElement && item.offsetParent !== null
      )
    );
  }

  /**
   * ArrowDown moves from the input into the results (Escape is handled by the dialog: it closes the panel).
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      const [first] = this.#navItems;
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  /**
   * Arrow keys move between result links; ArrowUp from the first one goes back to the input.
   * @param {KeyboardEvent} event
   */
  onResultsKeyDown = (event) => {
    const keys = ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'];
    if (!keys.includes(event.key)) return;

    const items = this.#navItems;
    const index = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (index === -1) return;

    event.preventDefault();
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const next = forward ? index + 1 : index - 1;

    if (next < 0) {
      this.refs.searchInput.focus();
    } else {
      items[Math.min(next, items.length - 1)]?.focus();
    }
  };

  /* ---------------------------------------------------------------- Search */

  /**
   * Debounced search: theme suggestions (local) + product results (Section Rendering API).
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (event && !event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#renderThemeSuggestions(searchTerm);
    this.#updateFooter(searchTerm);
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(PRODUCT_LIMIT));
    url.searchParams.set('resources[options][unavailable_products]', 'hide');

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();
    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query: searchTerm },
        promise: deferredPromise.promise,
      })
    );

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId,
        false,
        url,
        abortController.signal
      );

      if (abortController.signal.aborted) {
        deferredPromise.reject(new Error('Fetch aborted by user'));
        return;
      }

      // The text may have been cleared while the request was in flight
      if (!this.refs.searchInput.value.trim() || !resultsMarkup) {
        deferredPromise.resolve({ totalCount: 0 });
        return;
      }

      morph(predictiveSearchResults, resultsMarkup);
      this.#sortResults();
      this.#fitToScreen();

      const resultCount = predictiveSearchResults.querySelectorAll('.sab-search-card').length;
      deferredPromise.resolve({ totalCount: resultCount });
    } catch (error) {
      deferredPromise.reject(error);
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * @param {string} searchTerm
   */
  #updateFooter(searchTerm) {
    const { footer, viewAllButton } = this.refs;
    if (!footer || !viewAllButton) return;

    const template = this.dataset.seeAllTemplate || '__TERMS__';
    viewAllButton.textContent = template.replace('__TERMS__', searchTerm);
    footer.hidden = false;
  }

  /**
   * @param {string} searchTerm
   */
  async #renderThemeSuggestions(searchTerm) {
    const container = this.refs.themeSuggestions;
    if (!container) return;

    const themes = await loadThemes(this.dataset.themeSource);
    // Ignore if the text changed while the theme list was loading
    if (this.refs.searchInput.value.trim() !== searchTerm) return;

    renderThemeRow(container, matchThemes(searchTerm, themes, this.dataset.themeAliases), {
      heading: this.dataset.themeLabel,
      headingTag: 'h3',
      linkLabel: this.dataset.themeLinkLabel,
    });
    this.#fitToScreen();
  }

  /* ---------------------------------------------------------------- Empty state */

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    RecentlyViewed.clearProducts();

    const recent = this.querySelector('.sab-search-recent');
    if (!(recent instanceof HTMLElement)) return;

    recent.classList.add('removing');
    onAnimationEnd(recent, () => recent.remove());
  }

  /**
   * Fills the empty state's recently viewed slot (sold-out sets are left out by the section).
   */
  async #loadRecentlyViewed() {
    const slot = this.querySelector('#predictive-search-recent');
    if (!slot || !this.dataset.sectionId) return;

    const viewedProducts = RecentlyViewed.getProducts();
    if (!viewedProducts.length) {
      slot.replaceChildren();
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    const content = new DOMParser()
      .parseFromString(markup, 'text/html')
      .getElementById('predictive-search-recent-content');

    // The empty state may have been replaced by results in the meantime
    const currentSlot = this.querySelector('#predictive-search-recent');
    if (content && currentSlot) currentSlot.replaceChildren(...content.children);
    this.#fitToScreen();
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput, themeSuggestions, footer } = this.refs;

    this.search.cancel?.();
    searchInput.value = '';
    this.#hideResetButton();
    if (themeSuggestions) {
      themeSuggestions.hidden = true;
      themeSuggestions.replaceChildren();
    }
    if (footer) footer.hidden = true;

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
    if (abortController.signal.aborted) return;

    const emptyState = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('#predictive-search-empty');
    if (!emptyState) return;

    morph(predictiveSearchResults, `<div>${emptyState.outerHTML}</div>`);
    this.#loadRecentlyViewed();
    this.#fitToScreen();
  };

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
